import type { ReactNode } from 'react';
import { useShellTexts } from '../i18n/use-shell-texts';
import { Button } from './button';
import { Icon } from './icon';

/**
 * Loading, error and empty states, ported from portal-ui-lib's `feedback` components and drawn
 * as the legacy portal did: a large centred spinner, a small secondary retry button, and a
 * compact muted line for "no rows". Default wording is the catalogue's; a feature passes its own
 * (from its configuration) when it has one.
 */

export function LoadingState({ message }: { message?: string }) {
  const t = useShellTexts().feedback;
  return (
    <div
      role="status"
      className="flex flex-col items-center justify-center gap-2 p-6 text-ink-muted"
    >
      <Icon name="spinner" spin className="text-[3em]" />
      {/* The default wording only for assistive technology; a feature's own message shows. */}
      <span className={message ? 'text-sm' : 'sr-only'}>{message ?? t.loading}</span>
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message?: string | null;
  onRetry?: () => void;
}) {
  const t = useShellTexts().feedback;
  return (
    <div role="alert" className="flex flex-col items-center gap-3 p-5 text-center">
      <p className="mb-0 text-sm text-ink">
        <Icon name="exclamation-triangle" className="mr-1 text-danger" />
        {message ?? t.errorMessage}
      </p>
      {onRetry ? (
        <Button size="sm" icon="refresh" onClick={onRetry}>
          {t.retry}
        </Button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  message,
  subMessage,
  action,
}: {
  message?: string;
  subMessage?: string;
  /** A button under the message, e.g. "Create the first one". */
  action?: ReactNode;
}) {
  const t = useShellTexts().kit;
  return (
    <div className="flex flex-col items-start gap-2 p-3 text-sm text-ink-muted">
      <p className="mb-0">
        {message ?? t.noData}
        {subMessage ? <span className="block text-xs">{subMessage}</span> : null}
      </p>
      {action}
    </div>
  );
}
